// Xero Payroll (NZ) sub-panel — mounted inside XeroIntegration.jsx as a
// tab. Reuses mapping services and the reviewed, durable export workspace.
import { useEffect, useMemo, useState } from "react";
import PayrollExportCentre from "@/components/PayrollExportCentre";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Users, Zap, CheckCircle2, Save, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

function PayrollDiagnostics({ payrollReady, onReconnectNeeded, canReauthorise = false, refreshVersion }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [needsAuthorisation, setNeedsAuthorisation] = useState(false);
  const [mappingVersion, setMappingVersion] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [rates, setRates] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [users, setUsers] = useState([]);
  const [requiredIds, setRequiredIds] = useState([]);
  const [overtimeRequired, setOvertimeRequired] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [unavailable, setUnavailable] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [mapping, setMapping] = useState({
    user_map: {}, default_earnings_rate_id: "", overtime_earnings_rate_id: "",
    default_payroll_calendar_id: "", auto_sync: false,
  });
  const [saving, setSaving] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [pullingLeave, setPullingLeave] = useState(false);

  const loadAll = async () => {
    setLoading(true); setLoadError(""); setNeedsAuthorisation(false);
    try {
      const [emp, rt, cal, u, m] = await Promise.all([
        api.get("/xero/payroll/employees", {__flSkipReadCache: true}),
        api.get("/xero/payroll/earnings-rates", {__flSkipReadCache: true}),
        api.get("/xero/payroll/calendars", {__flSkipReadCache: true}),
        api.get("/users", {__flSkipReadCache: true}),
        api.get("/xero/payroll/mappings", {__flSkipReadCache: true}),
      ]);
      setRequiredIds(m.data?.required_user_ids || []);
      setOvertimeRequired(Boolean(m.data?.overtime_required));
      setEmployees(emp.data || []);
      setRates(rt.data || []);
      setCalendars(cal.data || []);
      setUnavailable(m.data?.unavailable_mappings || []);
      setRemovals([]);
      setUsers(m.data?.mapping_users || u.data || []);
      setMapping({
        user_map: (m.data && m.data.user_map) || {},
        default_earnings_rate_id: (m.data && m.data.default_earnings_rate_id) || "",
        overtime_earnings_rate_id: (m.data && m.data.overtime_earnings_rate_id) || "",
        default_payroll_calendar_id: (m.data && m.data.default_payroll_calendar_id) || "",
        auto_sync: !!(m.data && m.data.auto_sync),
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setNeedsAuthorisation(error.response?.status === 409 && typeof detail === "string"
        && /payroll.*(scopes|permissions)/i.test(detail));
      setLoadError("Payroll mappings could not be verified. Check the Xero connection and retry; settings have not been changed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (payrollReady) loadAll();
    else setLoading(false);
  }, [payrollReady, refreshVersion]);

  const employeeById = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);

  const isMatched = u => {
    const id = mapping.user_map[u.id];
    return employeeById[id] && !["inactive", "terminated"].includes((employeeById[id].status || "").toLowerCase())
      && Object.values(mapping.user_map).filter(value => value === id).length === 1;
  };
  const payrollUsers = users.filter(u => u.include_in_payroll !== false && requiredIds.includes(u.id));
  const unmatched = payrollUsers.filter(u => !isMatched(u));
  const matched = payrollUsers.filter(isMatched);
  const visibleUsers = advanced ? users : unmatched;
  const changeEligibility = async (u, included) => {
    try {
      await api.put(`/xero/payroll/employees/${u.id}/eligibility`, {include_in_payroll: included});
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update payroll inclusion"); }
  };

  const suggestion = u => {
    const name = (u.name || "").trim().toLowerCase();
    if (!name || users.filter(other => (other.name || "").trim().toLowerCase() === name).length !== 1) return null;
    const options = employees.filter(e => `${e.first_name} ${e.last_name}`.trim().toLowerCase() === name
      && !Object.values(mapping.user_map).includes(e.id)
      && !["inactive", "terminated"].includes((e.status || "").toLowerCase()));
    return options.length === 1 ? options[0] : null;
  };
  const confirmSuggestion = async (u, employee) => {
    setSaving(true);
    try {
      await api.post("/xero/payroll/mappings", {user_map: {...mapping.user_map, [u.id]: employee.id}});
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm employee match"); }
    finally { setSaving(false); }
  };
  const validRate = (id, type) => rates.some(r => r.id === id && r.current !== false && r.rate_type !== "FixedAmount" && (!r.units || ["hours", "hour"].includes(r.units.toLowerCase())) && (!r.type || r.type === type));
  const ordinaryNeeded = payrollUsers.length > 0 && !validRate(mapping.default_earnings_rate_id, "RegularEarnings");
  const overtimeNeeded = overtimeRequired && !validRate(mapping.overtime_earnings_rate_id, "OvertimeEarnings");
  const setMappingRow = (userId, emp) => {
    const next = { ...mapping.user_map };
    if (emp === "__none__" || !emp) delete next[userId];
    else next[userId] = emp;
    setMapping({ ...mapping, user_map: next });
  };

  const saveMappings = async () => {
    setSaving(true);
    try {
      await api.post("/xero/payroll/mappings", {
        user_map: mapping.user_map,
        remove_unavailable_mappings: removals,
        default_earnings_rate_id: mapping.default_earnings_rate_id || "",
        overtime_earnings_rate_id: mapping.overtime_earnings_rate_id || "",
        default_payroll_calendar_id: mapping.default_payroll_calendar_id || "",
        auto_sync: mapping.auto_sync,
      });
      toast.success("Payroll settings saved");
      await loadAll();
      setMappingVersion(v=>v+1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const autoMatch = async () => {
    setAutoMatching(true);
    try {
      const { data } = await api.post("/xero/payroll/auto-match");
      toast.success(`Matched ${data.matched?.length || 0} · unmatched ${data.unmatched?.length || 0}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-match failed");
    } finally {
      setAutoMatching(false);
    }
  };

  const pullLeaveBalances = async () => {
    setPullingLeave(true);
    try {
      const { data } = await api.post("/xero/payroll/leave-balances/sync");
      const skipped = data.skipped?.length || 0;
      toast.success(`Leave balances updated for ${data.updated} worker${data.updated === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Leave balance pull failed");
    } finally {
      setPullingLeave(false);
    }
  };

  if (!payrollReady || needsAuthorisation) {
    return (
      <div className="border-2 border-orange-300 bg-orange-50 dark:bg-orange-950/20 p-6 rounded-sm" data-testid="xero-payroll-reconnect">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF5F15] flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-black uppercase tracking-tight text-lg">Payroll permissions need authorisation</div>
            <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-1">
              Xero Accounting is connected, but Payroll NZ permissions need authorisation. Select the same Xero organisation when you reauthorise. Employee and earnings mappings, integration settings and export history will be preserved.
            </p>
            <Button
              onClick={onReconnectNeeded}
              disabled={!canReauthorise}
              className="mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
              data-testid="xero-payroll-reconnect-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reauthorise Xero
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-10 text-center text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading Xero payroll data…
      </div>
    );
  }

  if (loadError) return <div role="alert" className="text-sm p-4 text-amber-700 dark:text-amber-300">{loadError}<Button variant="outline" onClick={loadAll}>Retry loading</Button></div>;

  return (
    <div className="space-y-6" data-testid="xero-payroll-panel">
      <div className="hard-card p-4">
        <p>Payroll employees: {payrollUsers.length} · Matched: {matched.length} · Need attention: {unmatched.length}</p>
        <p className="text-xs text-zinc-500">Employees with approved hours awaiting transfer. Process Payroll as normal to send their hours.</p>
        {unmatched.length > 0 && <p>{unmatched.length} payroll {unmatched.length === 1 ? "employee needs" : "employees need"} a Xero match. <a className="underline" href="#payroll-exceptions">Fix {unmatched.length} {unmatched.length === 1 ? "employee" : "employees"}</a></p>}
        <label className="block mt-3"><input type="checkbox" checked={advanced} onChange={e => setAdvanced(e.target.checked)} /> Advanced</label>
      </div>
      <div hidden={!advanced && !ordinaryNeeded && !overtimeNeeded}>
      {/* --- Defaults --- */}
      <div className="hard-card p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">{advanced ? "Pay types and calendar" : "Pay types needing attention"}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div hidden={!advanced && !ordinaryNeeded}>
            <Label className="text-[10px] font-bold uppercase tracking-widest">Choose the Xero pay type for ordinary hours.</Label>
            <Select
              value={mapping.default_earnings_rate_id || "__none__"}
              onValueChange={(v) => setMapping({ ...mapping, default_earnings_rate_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="xero-payroll-rate">
                <SelectValue placeholder="Pick a rate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— pick a rate —</SelectItem>
                {rates.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} {r.type ? <span className="text-zinc-400 ml-1 text-[10px]">({r.type})</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Ordinary hours push against this rate.
            </div>
          </div>
          <div hidden={!advanced && !overtimeNeeded}>
            <Label className="text-[10px] font-bold uppercase tracking-widest">Choose the Xero pay type for overtime hours.</Label>
            <Select
              value={mapping.overtime_earnings_rate_id || "__none__"}
              onValueChange={(v) => setMapping({ ...mapping, overtime_earnings_rate_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="xero-payroll-ot-rate">
                <SelectValue placeholder="Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— choose when sending overtime hours —</SelectItem>
                {rates.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} {r.type ? <span className="text-zinc-400 ml-1 text-[10px]">({r.type})</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Overtime hours (per your Company Settings rules) push as separate lines against this rate.
            </div>
          </div>
          <div hidden={!advanced}>
            <Label className="text-[10px] font-bold uppercase tracking-widest">Default payroll calendar</Label>
            <Select
              value={mapping.default_payroll_calendar_id || "__none__"}
              onValueChange={(v) => setMapping({ ...mapping, default_payroll_calendar_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="xero-payroll-calendar">
                <SelectValue placeholder="Optional — use employee's own" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— use each employee's own calendar —</SelectItem>
                {calendars.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} <span className="text-zinc-400 ml-1 text-[10px]">({c.type})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div style={advanced ? undefined : {display: "none"}} className="mt-3 flex flex-wrap items-center justify-between gap-2 p-3 border border-zinc-200 rounded-sm">
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm font-bold">Leave balances from Xero</div>
            <div className="text-[11px] text-zinc-500">
              Pull each mapped worker's annual / sick / bereavement balances straight from Xero (overwrites Fieldlink's figures — hour balances convert at 8h = 1 day).
            </div>
          </div>
          <Button
            variant="outline"
            onClick={pullLeaveBalances}
            disabled={pullingLeave || !users.some(isMatched) || !canReauthorise}
            className="rounded-sm uppercase tracking-wider font-bold text-xs border-zinc-400"
            data-testid="xero-pull-leave-btn"
          >
            {pullingLeave ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {pullingLeave ? "Pulling…" : "Pull leave balances"}
          </Button>
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            onClick={saveMappings}
            disabled={saving || !canReauthorise}
            className="rounded-sm bg-zinc-900 hover:bg-black text-white uppercase tracking-wider font-bold text-xs"
            data-testid="xero-payroll-save"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>

      </div>
      {/* --- Employee mapping --- */}
      <div id="payroll-exceptions" className="hard-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{advanced ? "Full employee mappings and payroll inclusion" : "Employees needing a Xero match"}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {advanced ? `${employees.length} Xero employees available` : "Confirm a suggested match or select another Xero employee."}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={autoMatch}
            disabled={autoMatching || !canReauthorise}
            className="rounded-sm uppercase tracking-wider font-bold text-xs"
            data-testid="xero-payroll-automatch"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {autoMatching ? "Matching…" : "Auto-match by email"}
          </Button>
        </div>

        <Button variant="outline" onClick={loadAll} disabled={loading}>Refresh Xero employees and mappings</Button>
        {advanced && unavailable.map((item, index) => <div key={item.token} className="text-sm my-2">
          Unavailable mapping {index + 1}: {item.reason}. Preserved until explicitly removed.
          <Button variant="outline" disabled={removals.includes(item.token)} onClick={() => setRemovals(v => [...v, item.token])}>
            {removals.includes(item.token) ? "Removal pending — save settings" : "Remove obsolete mapping"}
          </Button>
        </div>)}
        {visibleUsers.length === 0 ? (
          <div className="text-xs text-zinc-500 italic p-4 border border-dashed border-zinc-300 rounded-sm text-center">
            No payroll employees need a match.
          </div>
        ) : (
          <div className="border border-zinc-200 divide-y divide-zinc-100 rounded-sm">
            {visibleUsers.map((u) => {
              const empId = mapping.user_map[u.id];
              const emp = empId ? employeeById[empId] : null;
              const suggested = !isMatched(u) ? suggestion(u) : null;
              return (
                <div key={u.id} className="p-2.5 grid grid-cols-12 gap-2 items-center hover:bg-zinc-50 dark:hover:bg-zinc-800" data-testid={`xero-payroll-map-${u.id}`}>
                  <div className="col-span-4">
                    <div className="text-sm font-bold truncate flex items-center gap-1">
                      {emp ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" /> : <Users className="w-3 h-3 text-zinc-400 shrink-0" />}
                      {u.name}{(u.suspended_at || u.is_active === false) ? " · inactive" : " · active"}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{u.email}</div>
                    {advanced && <label className="text-xs"><input type="checkbox" checked={u.include_in_payroll !== false} disabled={!canReauthorise} onChange={e => changeEligibility(u, e.target.checked)} /> Include in Payroll</label>}
                  </div>
                  <div className="col-span-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    {emp && !["inactive", "terminated"].includes((emp.status || "").toLowerCase()) ? "Mapped" : empId ? "Xero employee unavailable — remap" : "Missing — select employee"}
                  </div>
                  <div className="col-span-6">
                    {suggested && <Button variant="outline" disabled={saving || !canReauthorise} onClick={() => confirmSuggestion(u, suggested)}>Confirm {suggested.first_name} {suggested.last_name}</Button>}
                    <Select
                      value={empId || "__none__"}
                      onValueChange={(v) => setMappingRow(u.id, v)}
                    >
                      <SelectTrigger className="rounded-sm border-zinc-300 h-8 text-xs" data-testid={`xero-payroll-select-${u.id}`}>
                        <SelectValue placeholder="Pick a Xero employee">
                          {emp ? (
                            <span>{emp.first_name} {emp.last_name} <span className="text-zinc-400 ml-1 font-mono text-[10px]">({emp.email || "no email"})</span></span>
                          ) : "— no Xero match —"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— no Xero match —</SelectItem>
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.id} disabled={Object.entries(mapping.user_map).some(([uid, eid]) => uid !== u.id && eid === e.id) || ["inactive", "terminated"].includes((e.status || "").toLowerCase())}>
                            {e.first_name} {e.last_name} <span className="text-zinc-400 ml-1 font-mono text-[10px]">({e.email || "no email"})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Button onClick={saveMappings} disabled={saving || !canReauthorise}>Save employee matches</Button>
      {advanced && <PayrollExportCentre key={mappingVersion} />}

    </div>
  );
}

export function XeroPayrollPanel(props) {
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [automatic, setAutomatic] = useState(false);
  const [automaticBusy, setAutomaticBusy] = useState(false);
  const [automaticError, setAutomaticError] = useState("");
  useEffect(() => { api.get("/xero/payroll/automatic", {__flSkipReadCache: true}).then(({data}) => setAutomatic(Boolean(data.enabled))).catch(() => setAutomaticError("Automatic transfer setting unavailable. Refresh to try again.")); }, []);
  const changeAutomatic = async enabled => {
    setAutomaticBusy(true); setAutomaticError("");
    try { const {data} = await api.put("/xero/payroll/automatic", {enabled}); setAutomatic(Boolean(data.enabled)); setRefreshVersion(v => v + 1); }
    catch (e) { setAutomaticError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not update automatic transfer. Try again."); }
    finally { setAutomaticBusy(false); }
  };
  return <div className="space-y-4">
      <div className="hard-card p-4 flex items-center gap-3">
        <Switch id="automatic-payroll" checked={automatic} disabled={automaticBusy || !props.canReauthorise} onCheckedChange={changeAutomatic} className="data-[state=checked]:bg-[#FF5F15]" />
        <div><Label htmlFor="automatic-payroll">Send payroll hours automatically</Label>
          <p className="text-xs text-zinc-500 mt-1">When enabled, processing payroll in Fieldlink automatically sends approved employee hours to Xero Payroll.</p>
          {automaticError && <p role="alert" className="text-sm text-amber-700 mt-2">{automaticError}</p>}
        </div>
      </div>
    <PayrollDiagnostics {...props} refreshVersion={refreshVersion} />
  </div>;
}
